import * as React from "react";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

// Primary action + chevron menu in one pill (Approve ▾ Review / Resubmit /
// Reject, Mark paid ▾ ...) - shared by MyApprovals.tsx and ApproverQueue.tsx
// so every row's split button looks and behaves the same.
//
// Only the chevron is the menu trigger. Making the whole pill the trigger
// (to get the menu as wide as the pill) opened the menu on every click of
// the primary action too - Radix opens on pointerdown, before the primary
// button's own click handler runs - and left the menu unreachable by
// keyboard, since the wrapper was a plain div. The menu instead takes the
// pill's measured width when it opens.
export function SplitActionButton({
  label,
  icon,
  onClick,
  disabled,
  menuLabel = "More actions",
  children
}: {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  menuLabel?: string;
  /** DropdownMenuItem elements. */
  children: React.ReactNode;
}) {
  const pillRef = React.useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | undefined>();

  return (
    <div ref={pillRef} className="inline-flex">
      <Button
        size="sm"
        className="h-8 rounded-r-none"
        disabled={disabled}
        onClick={onClick}
      >
        {icon}
        {label}
      </Button>
      <DropdownMenu
        onOpenChange={(open) => {
          if (open) setMenuWidth(pillRef.current?.offsetWidth);
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            className="h-8 w-7 rounded-l-none border-l border-primary-foreground/20 p-0"
            disabled={disabled}
          >
            <span className="sr-only">{menuLabel}</span>
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        {/* The menu's own min-w-[8rem] still applies, so a very narrow
            pill can't squeeze "Resubmit" into an unreadable column. */}
        <DropdownMenuContent align="end" style={{ width: menuWidth }}>
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
